/**
 * Google Sheet Deployment Settings Schema
 *
 * Zod schema for Deployment.deploymentSettings when type = GOOGLE_SHEETS.
 * Single source of truth for Google Sheet deployment configuration.
 */

import { z } from "zod";

// ============================================================
// MAIN SCHEMA (Complete Google Sheet Deployment Settings)
// ============================================================

/**
 * Tracks a single script installation in a spreadsheet
 */
export const GoogleSheetInstallationSchema = z.object({
  spreadsheetId: z.string(),
  spreadsheetUrl: z.string(),
  scriptId: z.string(),
  scriptUrl: z.string(),
  functionName: z.string(),
  version: z.number().int().positive(),
  oauthConnectionId: z.string(),
  installedAt: z.string(),
  updatedAt: z.string().optional()
});

export type GoogleSheetInstallation = z.infer<typeof GoogleSheetInstallationSchema>;

/**
 * Complete Google Sheet Deployment Settings Schema
 *
 * This is the SINGLE SOURCE OF TRUTH for Deployment.deploymentSettings
 * when type = "GOOGLE_SHEETS". Stored as JSON in Prisma, validated with this schema.
 *
 * NOTE: agentId is NOT in deploymentSettings! It's a Deployment column (FK to Agent).
 */
export const GoogleSheetDeploymentSettingsSchema = z.object({
  // Skill names available for this deployment
  skillNames: z.array(z.string()),

  // Google Spreadsheet ID (from URL) - legacy, kept for backwards compat
  spreadsheetId: z.string(),

  // Installed spreadsheets (script installations log)
  installations: z.array(GoogleSheetInstallationSchema).optional()
});

export type GoogleSheetDeploymentSettings = z.infer<typeof GoogleSheetDeploymentSettingsSchema>;

// ============================================================
// DEFAULTS (Single Source of Truth)
// ============================================================

/**
 * Default values for creating a new Google Sheet deployment.
 * Used to initialize forms and ensure no missing fields.
 *
 * Note: spreadsheetId is required but defaults to empty - user must provide it.
 */
export const DEFAULT_GOOGLE_SHEET_SETTINGS: GoogleSheetDeploymentSettings = {
  skillNames: ["Chat"], // Default: Enable Chat skill (most agents have this)
  spreadsheetId: "" // Required - user must provide
};

// ============================================================
// FACTORY FUNCTION (Type-Safe Creation)
// ============================================================

/**
 * Create complete GOOGLE_SHEETS deployment settings with defaults.
 *
 * This is the RECOMMENDED way to create Google Sheet deployment settings.
 * Ensures all required fields are present with sensible defaults.
 *
 * NOTE: agentId is now Deployment column (FK to Agent).
 * This function returns only the settings JSON - agentId is passed separately
 * when creating/updating a Deployment.
 *
 * @param overrides - Partial settings to override defaults (optional)
 * @returns Complete, type-safe GoogleSheetDeploymentSettings
 *
 * @example
 * // Create with defaults (spreadsheetId must be set later)
 * const settings = createGoogleSheetDeploymentSettings();
 *
 * @example
 * // Create with spreadsheet ID
 * const settings = createGoogleSheetDeploymentSettings({
 *   spreadsheetId: "1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms",
 *   skillNames: ["search", "analyze"]
 * });
 */
export function createGoogleSheetDeploymentSettings(
  overrides?: Partial<GoogleSheetDeploymentSettings>
): GoogleSheetDeploymentSettings {
  if (!overrides) return { ...DEFAULT_GOOGLE_SHEET_SETTINGS };

  return {
    skillNames: overrides.skillNames ?? DEFAULT_GOOGLE_SHEET_SETTINGS.skillNames,
    spreadsheetId: overrides.spreadsheetId ?? DEFAULT_GOOGLE_SHEET_SETTINGS.spreadsheetId
  };
}
